import React from "react";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../requests/shared/helpers/DrawerFooterContainerLarge";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";

// API calls happen in the parent (section drawer) — this drawer is controlled
// via the `onSubmit` callback so the same UI can persist to the backend OR
// stash locally as a draft during section creation.


// Shared input styling — matches the violet-focus / rounded-8 pattern used
// across other drawer forms in the app (see ForecastAdjustmentDrawer,
// ShiftSingleInputModal). Spread when merging with field-specific overrides.
const INPUT_SX = {
  backgroundColor: "white",
  borderRadius: "8px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
};

const SELECT_SX = {
  backgroundColor: "white",
  borderRadius: "8px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
};

const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "scale", label: "Scale" },
  { value: "yes_no", label: "Yes/No" },
  { value: "single_select", label: "Single-select" },
];

const VALUE_KEY_BY_FIELD_TYPE = {
  text: "text",
  scale: "number",
  yes_no: "bool",
  single_select: "option",
};

const OPERATOR_OPTIONS = [
  { value: "equals", label: "equals" },
  { value: "less_than", label: "is less than" },
  { value: "greater_than", label: "is greater than" },
];


function defaultConfigForType(type) {
  switch (type) {
    case "text":
      return { rows: 4 };
    case "scale":
      return { min: 1, max: 10, anchor_low: "", anchor_high: "", low_threshold: null };
    case "yes_no":
      return {};
    case "single_select":
      return { options: ["Option 1", "Option 2"] };
    default:
      return {};
  }
}


function CheckInTemplateFieldDrawer(props) {
  // Props:
  // - open / onClose: drawer visibility
  // - field: existing field (backend or draft) when editing; null when creating
  // - template: used to enumerate candidate parent fields for conditional rules
  // - onSubmit(fieldData): parent-supplied save handler. Receives the
  //   assembled payload (label, help_text, field_type, config, conditional).
  //   Should return a promise; throw to surface an error.
  // - hideConditional: gate the conditional UI off — used in section-create
  //   mode where draft fields can't reference each other across the API.
  const {
    open,
    onClose,
    field,
    template,
    onSubmit,
    hideConditional,
  } = props;

  const [label, setLabel] = React.useState("");
  const [helpText, setHelpText] = React.useState("");
  const [fieldType, setFieldType] = React.useState("text");
  const [config, setConfig] = React.useState({});
  const [conditionalOn, setConditionalOn] = React.useState("");
  const [conditionalOp, setConditionalOp] = React.useState("equals");
  const [conditionalValuePrimitive, setConditionalValuePrimitive] = React.useState("");

  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Remember user's edits per field type so flipping the dropdown back and
  // forth (intentionally or by accident) doesn't wipe their min/max/anchors.
  const configByTypeRef = React.useRef({});

  // Hydrate every time the drawer opens (and again when the target field
  // switches). Without `open` in the deps, a Cancel followed by re-opening
  // the same field would show the user's uncommitted edits as if they were
  // saved. Handles three cases:
  //   1. Open + existing field (backend or draft) → hydrate from field
  //   2. Open + no field (create mode) → reset to defaults
  //   3. Not open → no-op
  React.useEffect(() => {
    if (!open) return;
    if (field) {
      setLabel(field.label || "");
      setHelpText(field.help_text || "");
      setFieldType(field.field_type);
      const initialConfig = field.config || defaultConfigForType(field.field_type);
      setConfig(initialConfig);
      configByTypeRef.current = { [field.field_type]: initialConfig };
      // Conditional state is only meaningful when the conditional UI is
      // shown. In section-create mode (hideConditional=true) draft fields
      // can't reference each other across the API, so leave it cleared.
      if (!hideConditional) {
        setConditionalOn(field.conditional_on_field_uuid || "");
        setConditionalOp(field.conditional_operator || "equals");
        let parentType = null;
        if (field.conditional_on_field_uuid && template?.sections) {
          for (const sec of template.sections) {
            const parent = (sec.fields || []).find(
              (f) => f.uuid === field.conditional_on_field_uuid,
            );
            if (parent) {
              parentType = parent.field_type;
              break;
            }
          }
        }
        const v = field.conditional_value;
        const expectedKey = VALUE_KEY_BY_FIELD_TYPE[parentType];
        if (v && typeof v === "object" && expectedKey) {
          const primitive = v[expectedKey];
          setConditionalValuePrimitive(primitive != null ? String(primitive) : "");
        } else {
          setConditionalValuePrimitive("");
        }
      } else {
        setConditionalOn("");
        setConditionalOp("equals");
        setConditionalValuePrimitive("");
      }
    } else {
      // Create mode — fresh defaults.
      setLabel("");
      setHelpText("");
      setFieldType("text");
      setConfig(defaultConfigForType("text"));
      configByTypeRef.current = { text: defaultConfigForType("text") };
      setConditionalOn("");
      setConditionalOp("equals");
      setConditionalValuePrimitive("");
    }
    setError(null);
    // template/hideConditional intentionally not in deps — we hydrate once
    // per drawer open / field switch, not on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, field?.uuid]);

  // Track config changes for the current type so we can restore on swap.
  React.useEffect(() => {
    configByTypeRef.current[fieldType] = config;
  }, [config, fieldType]);

  const handleFieldTypeChange = (newType) => {
    if (newType === fieldType) return;
    // Stash current type's work, then restore the other type's last value
    // (or its default if we've never set one in this session).
    configByTypeRef.current[fieldType] = config;
    setFieldType(newType);
    const restored = configByTypeRef.current[newType];
    setConfig(restored ?? defaultConfigForType(newType));
  };

  const handleConditionalOnChange = (newUuid) => {
    if (newUuid === conditionalOn) return;
    setConditionalOn(newUuid);
    // The stored primitive belongs to the OLD parent's type. Leaving it in
    // place lets it silently coerce when saved — e.g. "7" from a scale
    // parent becomes {bool: false} after switching to a yes/no parent
    // (because `"7" === "true"` is false). Force the admin to pick again.
    setConditionalValuePrimitive("");
    // less_than / greater_than only apply to scale parents. If the new
    // parent isn't scale, reset operator so it doesn't fail backend
    // validation on save.
    const newParent = candidateParents.find((f) => f.uuid === newUuid);
    if (newParent && newParent.field_type !== "scale") {
      setConditionalOp("equals");
    }
  };

  // All other fields in the template that this one could be conditional on.
  // Cross-section is allowed, but can't reference self. Text fields are
  // excluded — `equals` against free-form text is too brittle (capitalization,
  // whitespace, phrasing all break the match), and less_than / greater_than
  // are scale-only. In create mode (field is null) we include everything;
  // nothing to exclude.
  const candidateParents = [];
  if (!hideConditional) {
    (template?.sections || []).forEach((s) => {
      (s.fields || []).forEach((f) => {
        if (field && f.uuid === field.uuid) return;
        if (f.field_type === "text") return;
        candidateParents.push({ ...f, sectionTitle: s.title });
      });
    });
  }

  // Whether any other field is conditional on THIS field — blocks type
  // changes (mirrors backend restriction). Only meaningful for backend
  // fields with a stable uuid; drafts and brand-new fields are never
  // depended on yet.
  const hasDependents =
    field?.uuid && !field.uuid.startsWith("draft-")
      ? candidateParents.some((f) => f.conditional_on_field_uuid === field.uuid)
      : false;

  const parentField = candidateParents.find((f) => f.uuid === conditionalOn);

  // ----- Render type-specific config -----

  const renderTypeConfig = () => {
    if (fieldType === "text") {
      return (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Rows (1–20)
            </label>
            <TextField
              type="number"
              size="small"
              fullWidth
              value={config.rows ?? 4}
              onChange={(evt) =>
                setConfig({ ...config, rows: Number(evt.target.value) || 1 })
              }
              inputProps={{ min: 1, max: 20 }}
              InputProps={{ sx: INPUT_SX }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Placeholder (optional)
            </label>
            <TextField
              size="small"
              fullWidth
              value={config.placeholder || ""}
              onChange={(evt) =>
                setConfig({ ...config, placeholder: evt.target.value })
              }
              InputProps={{ sx: INPUT_SX }}
            />
          </div>
        </div>
      );
    }

    if (fieldType === "scale") {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Min
              </label>
              <TextField
                type="number"
                size="small"
                fullWidth
                value={config.min ?? 1}
                onChange={(evt) =>
                  setConfig({ ...config, min: Number(evt.target.value) })
                }
                InputProps={{ sx: INPUT_SX }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Max
              </label>
              <TextField
                type="number"
                size="small"
                fullWidth
                value={config.max ?? 10}
                onChange={(evt) =>
                  setConfig({ ...config, max: Number(evt.target.value) })
                }
                InputProps={{ sx: INPUT_SX }}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Anchor at min
              </label>
              <TextField
                size="small"
                fullWidth
                value={config.anchor_low || ""}
                onChange={(evt) =>
                  setConfig({ ...config, anchor_low: evt.target.value })
                }
                placeholder="e.g. Unclear, unsupported"
                InputProps={{ sx: INPUT_SX }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Anchor at max
              </label>
              <TextField
                size="small"
                fullWidth
                value={config.anchor_high || ""}
                onChange={(evt) =>
                  setConfig({ ...config, anchor_high: evt.target.value })
                }
                placeholder="e.g. Clear, supported"
                InputProps={{ sx: INPUT_SX }}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Low-score threshold (optional)
            </label>
            <TextField
              type="number"
              size="small"
              value={config.low_threshold ?? ""}
              onChange={(evt) => {
                const raw = evt.target.value;
                setConfig({
                  ...config,
                  low_threshold: raw === "" ? null : Number(raw),
                });
              }}
              placeholder="Score below this is highlighted as low"
              sx={{ width: "100%" }}
              InputProps={{ sx: INPUT_SX }}
            />
          </div>
        </div>
      );
    }

    if (fieldType === "yes_no") {
      return (
        <p className="text-xs italic text-slate-500">
          No additional configuration needed.
        </p>
      );
    }

    if (fieldType === "single_select") {
      const options = Array.isArray(config.options) ? config.options : [];
      const setOptions = (newOpts) => setConfig({ ...config, options: newOpts });
      return (
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Options
          </label>
          <div className="space-y-2">
            {options.map((opt, idx) => (
              <div key={idx} className="flex gap-2">
                <TextField
                  size="small"
                  fullWidth
                  value={opt}
                  onChange={(evt) => {
                    const next = [...options];
                    next[idx] = evt.target.value;
                    setOptions(next);
                  }}
                  InputProps={{ sx: INPUT_SX }}
                />
                <Tooltip title="Remove option" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setOptions(options.filter((_, i) => i !== idx))}
                  >
                    <DeleteOutlinedIcon
                      style={{ fontSize: "18px" }}
                      className="text-slate-500 hover:text-rose-600"
                    />
                  </IconButton>
                </Tooltip>
              </div>
            ))}
          </div>
          <button
            onClick={() => setOptions([...options, ""])}
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-violet-600 hover:text-violet-700"
          >
            <AddIcon style={{ fontSize: "16px" }} />
            Add option
          </button>
        </div>
      );
    }

    return null;
  };

  // ----- Render conditional value input (depends on parent type) -----

  const renderConditionalValueInput = () => {
    if (!parentField) return null;
    const pType = parentField.field_type;

    if (pType === "scale") {
      return (
        <TextField
          type="number"
          size="small"
          fullWidth
          value={conditionalValuePrimitive}
          onChange={(evt) => setConditionalValuePrimitive(evt.target.value)}
          InputProps={{ sx: INPUT_SX }}
        />
      );
    }
    if (pType === "yes_no") {
      return (
        <Select
          size="small"
          fullWidth
          value={conditionalValuePrimitive}
          onChange={(evt) => setConditionalValuePrimitive(evt.target.value)}
          sx={SELECT_SX}
        >
          <MenuItem value="true">Yes (true)</MenuItem>
          <MenuItem value="false">No (false)</MenuItem>
        </Select>
      );
    }
    if (pType === "single_select") {
      const opts = parentField.config?.options || [];
      return (
        <Select
          size="small"
          fullWidth
          value={conditionalValuePrimitive}
          onChange={(evt) => setConditionalValuePrimitive(evt.target.value)}
          sx={SELECT_SX}
        >
          {opts.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </Select>
      );
    }
    // text
    return (
      <TextField
        size="small"
        fullWidth
        value={conditionalValuePrimitive}
        onChange={(evt) => setConditionalValuePrimitive(evt.target.value)}
        InputProps={{ sx: INPUT_SX }}
      />
    );
  };

  // ----- Save -----

  const buildConditionalPayload = () => {
    if (!conditionalOn) return null;
    if (!parentField) return null;
    const pType = parentField.field_type;
    let value;
    if (pType === "scale") {
      const n = Number(conditionalValuePrimitive);
      if (Number.isNaN(n)) return undefined;  // sentinel: invalid
      value = { number: n };
    } else if (pType === "yes_no") {
      value = { bool: conditionalValuePrimitive === "true" };
    } else if (pType === "single_select") {
      value = { option: conditionalValuePrimitive };
    } else {
      value = { text: conditionalValuePrimitive };
    }
    return {
      on_field_uuid: conditionalOn,
      operator: conditionalOp,
      value,
    };
  };

  const handleSave = async () => {
    if (!label.trim()) {
      setError("Label is required.");
      return;
    }
    // Block if the admin enabled a conditional rule but never picked a value.
    // Without this, an untouched Select/input would silently serialize as
    // {bool: false} / {number: 0} / {option: ""} — creating an unintended
    // rule (e.g. follow-up tied to "No") without any visible error.
    if (!hideConditional) {
      if (conditionalOn && !conditionalValuePrimitive.toString().trim()) {
        setError("Please select or enter a conditional value.");
        return;
      }
    }
    const cond = hideConditional ? null : buildConditionalPayload();
    if (cond === undefined) {
      setError("Conditional value couldn't be parsed.");
      return;
    }
    // Disallow less_than/greater_than for non-scale parents — mirrors backend.
    if (cond && cond.operator !== "equals" && parentField?.field_type !== "scale") {
      setError("'Less than' / 'greater than' only work against scale fields.");
      return;
    }

    // The drawer is controlled — assemble the field data and hand off to the
    // parent, which decides whether to POST or stash locally.
    const fieldData = {
      label: label.trim(),
      help_text: helpText,
      field_type: fieldType,
      config,
      conditional: cond,
    };

    setSaving(true);
    setError(null);
    try {
      await onSubmit(fieldData);
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.system?.message || e?.message || "Couldn't save field.",
      );
    } finally {
      setSaving(false);
    }
  };

  const headerTitle = field ? `Edit field: ${field.label}` : "New field";
  const saveButtonLabel = field ? "Save Field" : "Create Field";

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className="w-[520px] max-w-screen h-full bg-slate-50 relative">
        <div className="max-h-screen overflow-auto">
          <DrawerHeader title={headerTitle} onClose={onClose} />

          <div className="px-6 py-4 space-y-5">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Label
              </label>
              <TextField
                value={label}
                onChange={(evt) => setLabel(evt.target.value)}
                fullWidth
                size="small"
                InputProps={{ sx: INPUT_SX }}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Help text (optional)
              </label>
              <TextField
                value={helpText}
                onChange={(evt) => setHelpText(evt.target.value)}
                fullWidth
                size="small"
                multiline
                rows={2}
                InputProps={{ sx: { ...INPUT_SX, fontSize: "13px" } }}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Field type
              </label>
              <Tooltip
                title={
                  hasDependents
                    ? "Other fields depend on this one conditionally. Remove or re-point them before changing the type."
                    : ""
                }
                arrow
              >
                <span>
                  <Select
                    value={fieldType}
                    onChange={(evt) => handleFieldTypeChange(evt.target.value)}
                    disabled={hasDependents}
                    size="small"
                    fullWidth
                    sx={SELECT_SX}
                  >
                    {FIELD_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </span>
              </Tooltip>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-slate-700 uppercase mb-3">
                Type-specific configuration
              </h4>
              {renderTypeConfig()}
            </div>

            {/* Conditional rules are hidden in section-create mode: draft
                fields don't have stable backend UUIDs other drafts could
                reference, so we defer this configuration to after the
                section is saved (when each field has a real uuid). */}
            {!hideConditional && (
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <h4 className="text-xs font-semibold text-slate-700 uppercase mb-3">
                  Conditional rule
                </h4>
                <p className="text-xs text-slate-500 mb-2">
                  Show this field only when another field's answer matches.
                </p>
                <div className="space-y-2">
                  <Select
                    size="small"
                    fullWidth
                    value={conditionalOn}
                    onChange={(evt) => handleConditionalOnChange(evt.target.value)}
                    displayEmpty
                    sx={SELECT_SX}
                  >
                    <MenuItem value="">— Always shown —</MenuItem>
                    {candidateParents.map((f) => (
                      <MenuItem key={f.uuid} value={f.uuid}>
                        [{f.sectionTitle}] {f.label} ({f.field_type})
                      </MenuItem>
                    ))}
                  </Select>

                  {conditionalOn && (
                    <div className="grid grid-cols-2 gap-2">
                      <Select
                        size="small"
                        fullWidth
                        value={conditionalOp}
                        onChange={(evt) => setConditionalOp(evt.target.value)}
                        sx={SELECT_SX}
                      >
                        {OPERATOR_OPTIONS.map((opt) => (
                          <MenuItem
                            key={opt.value}
                            value={opt.value}
                            disabled={
                              opt.value !== "equals" &&
                              parentField?.field_type !== "scale"
                            }
                          >
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {renderConditionalValueInput()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {error && (
              <p className="text-sm text-rose-600">{error}</p>
            )}
          </div>

          <div className="h-[160px]"></div>
        </div>

        <div>
          <DrawerFooterContainerLarge>
            <div className="w-full">
              <LoadingButton
                variant="contained"
                loading={saving}
                loadingPosition="end"
                endIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{
                  width: "100%",
                  textTransform: "none",
                  borderRadius: "12px",
                  paddingY: "14px",
                  backgroundColor: "#6d28d9",
                  "&:hover": { backgroundColor: "#5b21b6" },
                  fontSize: "16px",
                }}
              >
                {saveButtonLabel}
              </LoadingButton>
            </div>
            <div className="w-full mt-3">
              <ButtonOutlined text="Cancel" onClick={onClose} />
            </div>
          </DrawerFooterContainerLarge>
        </div>
      </div>
    </Drawer>
  );
}

export default CheckInTemplateFieldDrawer;
